<script lang="ts">
  import { onMount } from 'svelte';
  import { api } from '../api/client';
  import type { ApiKey } from '../api/types';
  import PageHeader from './PageHeader.svelte';
  import { confirm } from '../stores/confirmDialog';
  import { age } from '../utils/time';
  import { date, t } from '../i18n/index.ts';

  // ApiKeys lists, creates, and deletes the account's API keys for scripts
  // and the MCP endpoint (spec 001 FR-115 to 001 FR-118). A created key's
  // value shows once. locked shows the list only, for a child's own account.
  let { locked = false }: { locked?: boolean } = $props();

  // presets fill the expiration date counted from today; Never empties it (001 FR-116).
  const presets: { label: string; days?: number; years?: number }[] = [
    { label: t('apiKeysIn30Days'), days: 30 },
    { label: t('apiKeysIn90Days'), days: 90 },
    { label: t('apiKeysInAYear'), years: 1 },
    { label: t('apiKeysNever') },
  ];

  let keys: ApiKey[] = $state([]);
  let error = $state('');
  let creating = $state(false);
  let name = $state('');
  // expiry is the date field's YYYY-MM-DD, empty for never.
  let expiry = $state('');
  // created is the value of the key just created, shown until dismissed.
  let created = $state('');
  let copied = $state(false);

  // localDate formats a local calendar day as the date field takes it;
  // toISOString would shift it across UTC midnight.
  function localDate(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  function preset(p: { days?: number; years?: number }) {
    if (!p.days && !p.years) {
      expiry = '';
      return;
    }
    const d = new Date();
    if (p.days) d.setDate(d.getDate() + p.days);
    if (p.years) d.setFullYear(d.getFullYear() + p.years);
    expiry = localDate(d);
  }

  async function load() {
    try {
      keys = await api.get<ApiKey[]>('/me/api-keys');
    } catch (e: any) {
      error = e.message;
    }
  }
  onMount(load);

  async function create(e: SubmitEvent) {
    e.preventDefault();
    error = '';
    // A key works through the end of its date in the member's time zone.
    const [y, m, d] = expiry.split('-').map(Number);
    const body: Record<string, unknown> = { name: name.trim() };
    if (expiry) body.expires_at = new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
    try {
      const got = await api.post<ApiKey & { key: string }>('/me/api-keys', body);
      created = got.key;
      copied = false;
      creating = false;
      name = '';
      expiry = '';
      await load();
    } catch (err: any) {
      error = err.message;
    }
  }

  async function copy() {
    await navigator.clipboard?.writeText(created).catch(() => {});
    copied = true;
  }

  async function remove(k: ApiKey) {
    if (!(await confirm(t('apiKeysDeleteConfirm', { name: k.name })))) return;
    created = '';
    await api.delete(`/me/api-keys/${k.id}`).catch((e: any) => (error = e.message));
    await load();
  }

  // ends says when a key stops working, or that it stopped.
  function ends(k: ApiKey): string {
    if (!k.expires_at) return t('apiKeysNoExpiry');
    const at = new Date(k.expires_at);
    return at.getTime() < Date.now() ? t('apiKeysExpiredOn', { date: date(at) }) : t('apiKeysExpiresOn', { date: date(at) });
  }

  const expired = (k: ApiKey) => !!k.expires_at && new Date(k.expires_at).getTime() < Date.now();
</script>

<div class="card p-5 mb-6">
  <PageHeader title={t('apiKeysTitle')} level="section">
    {#if !locked && !creating}
      <button class="btn btn-secondary btn-sm" onclick={() => { creating = true; created = ''; }}><i aria-hidden="true" class="las la-plus mr-1"></i>{t('apiKeysCreate')}</button>
    {/if}
  </PageHeader>
  <p class="text-sm text-text-muted mb-3">{t('apiKeysAbout')}</p>
  {#if created}
    <div class="mb-3 p-3 rounded" style="background: var(--theme-surface0);">
      <p class="text-sm mb-2">{t('apiKeysShownOnce')}</p>
      <div class="flex gap-2">
        <input class="input w-full font-mono text-sm" value={created} readonly aria-label={t('apiKeysTitle')} onfocus={(e) => e.currentTarget.select()} />
        <button class="btn btn-primary btn-sm" onclick={copy}>{copied ? t('appCopied') : t('appCopy')}</button>
        <button class="btn btn-secondary btn-sm" onclick={() => (created = '')}>{t('appDone')}</button>
      </div>
    </div>
  {/if}
  {#if creating}
    <form class="mb-3 flex flex-col gap-3" onsubmit={create}>
      <label class="text-sm">
        {t('apiKeysName')}
        <input class="input w-full mt-1" required maxlength="100" bind:value={name} placeholder={t('apiKeysNameHint')} autocomplete="off" />
      </label>
      <label class="text-sm">
        {t('apiKeysExpiration')}
        <input class="input w-full mt-1" type="date" min={localDate(new Date())} bind:value={expiry} />
      </label>
      <div class="flex flex-wrap gap-2 -mt-1">
        {#each presets as p (p.label)}
          <button type="button" class="btn btn-secondary btn-sm" onclick={() => preset(p)}>{p.label}</button>
        {/each}
      </div>
      <div class="flex justify-end gap-2">
        <button type="button" class="btn btn-secondary" onclick={() => (creating = false)}>{t('appCancel')}</button>
        <button type="submit" class="btn btn-primary">{t('apiKeysCreate')}</button>
      </div>
    </form>
  {/if}
  {#if keys.length === 0}
    <p class="text-sm">{t('apiKeysNone')}</p>
  {:else}
    <ul>
      {#each keys as k (k.id)}
        <li class="py-2 flex items-center gap-3">
          <i aria-hidden="true" class="las la-key text-xl"></i>
          <span class="flex-1 min-w-0">
            <span class="font-medium break-all">{k.name}</span>
            <span class="block text-xs text-text-muted">
              <span class="font-mono">{k.prefix}…</span> ·
              {t('apiKeysCreatedOn', { date: date(new Date(k.created_at)) })} ·
              {k.last_used ? t('apiKeysLastUsed', { when: age(k.last_used) }) : t('apiKeysNotUsed')} ·
              <span style={expired(k) ? 'color: var(--theme-error);' : ''}>{ends(k)}</span>
            </span>
          </span>
          {#if !locked}
            <button class="btn btn-danger btn-sm" onclick={() => remove(k)} aria-label={t('apiKeysDeleteLabel', { name: k.name })}><i aria-hidden="true" class="las la-trash"></i></button>
          {/if}
        </li>
      {/each}
    </ul>
  {/if}
  {#if error}<p class="text-sm mt-2" style="color: var(--theme-error);">{error}</p>{/if}
</div>
